import {
  Button,
  ConfirmModal,
  Flex,
  Table,
  TableRowMenuCell,
  TitledText,
} from '@/components';
import { ProjectResourceGuard } from '@/features/authentication/_components/ProjectResourceGuard';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { useProjectPermission } from '@/features/authentication/_hooks/permission';
import { useProject, useUpdateRemark } from '@/features/project/_hooks/project';
import {
  DiscriminatedInspectionEvaluationModel,
  ProjectModel,
} from '@/features/project/projects/_models/project';
import { useId } from '@/hooks';
import { useLocaledAmount, useMessage } from '@/providers';
import { TABLE_ROW_MENU_WIDTH } from '@/utils';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import type { IndonesiaData } from '../../../_models/indonesia-data';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { AlurDanaFormDrawer } from './AlurDanaFormDrawer';

const PropsModel = ProjectModel.pick({
  fiscalYear: true,
  sheetType: true,
  inspectionEvaluation: true,
  aiEvaluation: true,
});

type Props = z.infer<typeof PropsModel>;

export const InspectionEvaluation: FC<Props> = (props) => {
  const { t } = useTranslation();
  const { id } = useId();
  const { unit: _unit } = useLocaledAmount();
  const { setDrawerState } = useProjectDetailDrawerState();
  const { data: project } = useProject(id);
  const { mutateAsync: updateProject } = useUpdateRemark();
  const editPermission = useProjectPermission(true);
  const [alurDanaDelete, setAlurDanaDelete] = useState<
    NonNullable<IndonesiaData['alurDana']>[number] | null
  >(null);
  const { openMessage } = useMessage();

  // const accountingTypes = useAccountingTypes();
  // const _generalAccountingTypeId = accountingTypes.data?.find(
  //   (accountingType) => accountingType.type === 'general',
  // )?.id;
  // const _accountingTypeOptions = useAccountingTypeOptions();
  // const _accountTitleOptions = useAccountTitleOptions();
  const parsed = DiscriminatedInspectionEvaluationModel.safeParse(props);

  // const [isLoading, setIsLoading] = useState(false);
  // const [aiReviewResult, setAiReviewResult] = useState<string | null>(null);

  // const handleAiReview = async () => {
  //   setIsLoading(true);
  //   try {
  //     const response = await axiosGet(`/api/projects/${id}/chat/`);
  //     setAiReviewResult(response.answer);
  //   } catch (_error) {
  //     setAiReviewResult(
  //       'AIレビューの取得に失敗しました。しばらく時間をおいてから再度お試しください。',
  //     );
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  if (!parsed.success || parsed.data.sheetType !== 'RS') {
    return null;
  }

  const {
    inspectionAndImprovementByAdministrativeJurisdiction,
    inspectionByExternalExpert: _inspectionByExternalExpert,
    processAndOpinionByAdministrativeReviewTeam:
      _processAndOpinionByAdministrativeReviewTeam,
    reflectionStatusBasedOnOpinion,
    summariesAndResponses: _summariesAndResponses,
    otherIndicatedItemsAndResponses: _otherIndicatedItemsAndResponses,
  } = parsed.data.inspectionEvaluation;

  const _hasReflectedAmountOfGeneralAccount =
    typeof reflectionStatusBasedOnOpinion?.reflectedAmountOfGeneralAccount ===
    'number';

  return (
    <Flex vertical gap={16}>
      <Flex justify="end">
        <ProjectResourceGuard onlyEdit>
          <PublishedProjectMutableGuard>
            {!project?.isLocked && (
              <Button
                type="primary"
                onClick={() => {
                  setDrawerState({
                    open: true,
                    type: 'inspection-evaluation',
                  });
                }}
              >
                {t('edit-inspection-evaluation')}
              </Button>
            )}
          </PublishedProjectMutableGuard>
        </ProjectResourceGuard>
      </Flex>

      <TitledText
        title={t('penjelasan-tentang-kegiatan-penerima-dana')}
        text={
          inspectionAndImprovementByAdministrativeJurisdiction?.analysisOfValidity
        }
      />

      <Flex justify="end">
        <ProjectResourceGuard onlyEdit>
          <PublishedProjectMutableGuard>
            {!project?.isLocked && (
              <Button
                type="primary"
                onClick={() => {
                  setDrawerState({
                    open: true,
                    type: 'alur-dana',
                  });
                }}
              >
                {t('edit')}
              </Button>
            )}
          </PublishedProjectMutableGuard>
        </ProjectResourceGuard>
      </Flex>

      <Table
        dataSource={project?.indonesiaData?.alurDana ?? []}
        columns={[
          { title: t('number'), dataIndex: 'number' },
          { title: t('penerima-dana'), dataIndex: 'penerimaDana', width: 150 },
          { title: t('ringkasan-kegiatan'), dataIndex: 'ringkasanKegiatan' },
          { title: t('jumlah-dana'), dataIndex: 'jumlahDana' },
          {
            title: t('metode-kontrak-dll'),
            dataIndex: 'metodeKontrakDll',
            width: 150,
          },
          {
            title: t('jumlah-peserta-tender'),
            dataIndex: 'jumlahPesertaTender',
            width: 150,
          },
          {
            title: t('tingkat-kemenangan-tender'),
            width: 150,
            dataIndex: 'tingkatKemenanganTender',
          },
          ...(!project?.isLocked && editPermission.data
            ? [
                {
                  key: 'actions',
                  width: TABLE_ROW_MENU_WIDTH,
                  render: (_, record) => {
                    return (
                      <PublishedProjectMutableGuard>
                        <Flex justify="center">
                          <TableRowMenuCell
                            onEdit={() => {
                              setDrawerState({
                                open: true,
                                type: 'alur-dana',
                                alurDanaId: record.id,
                              });
                            }}
                            items={[
                              {
                                key: 'delete',
                                label: t('delete'),
                                danger: true,
                                onClick: () => {
                                  setAlurDanaDelete(record);
                                },
                              },
                            ]}
                          />
                        </Flex>
                      </PublishedProjectMutableGuard>
                    );
                  },
                },
              ]
            : []),
        ]}
      />

      <AlurDanaFormDrawer />
      <ConfirmModal
        open={alurDanaDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-item-confirmation', {
          item: t('alur-dana'),
        })}
        onOk={async () => {
          try {
            updateProject({
              id,
              indonesiaData: {
                ...project?.indonesiaData,
                alurDana: project?.indonesiaData?.alurDana?.filter(
                  (alurDana) => alurDana.id !== alurDanaDelete?.id,
                ),
              },
            });
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setAlurDanaDelete(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => {
          setAlurDanaDelete(null);
        }}
        skipCheck
      />

      {/* <ContentWrapper
        title={t('inspection-and-improvement-by-administrative-jurisdiction')}
      >
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            style={{ marginBottom: 24 }}
            items={[
              <PinpointMarker
                key="analysis-of-validity"
                pinpoint={{
                  path: { project: id },
                  field: 'rs.analysis-of-validity',
                }}
              >
                <TitledText
                  title="20.予算活用の妥当性の分析"
                  text={
                    inspectionAndImprovementByAdministrativeJurisdiction?.analysisOfValidity
                  }
                />
              </PinpointMarker>,
              <PinpointMarker
                key="inspection-result"
                pinpoint={{
                  path: { project: id },
                  field: 'rs.inspection-result',
                }}
              >
                <TitledText
                  title={t('inspection-result')}
                  text={
                    inspectionAndImprovementByAdministrativeJurisdiction?.inspectionResult
                  }
                />
              </PinpointMarker>,
            ]}
          />

          <ContentItems
            items={[
              <PinpointMarker
                key="evaluation"
                pinpoint={{
                  path: { project: id },
                  field: 'rs.evaluation',
                }}
              >
                <TitledText
                  title={t('evaluation')}
                  text={
                    inspectionAndImprovementByAdministrativeJurisdiction?.evaluation
                  }
                />
              </PinpointMarker>,
              <PinpointMarker
                key="direction-of-improvement"
                pinpoint={{
                  path: { project: id },
                  field: 'rs.direction-of-improvement',
                }}
              >
                <TitledText
                  title={t('direction-of-improvement')}
                  text={
                    inspectionAndImprovementByAdministrativeJurisdiction?.directionOfImprovement
                  }
                />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
      </ContentWrapper> */}
    </Flex>
  );
};
